import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { useSites, SitePicker } from "@/components/wp/SitePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, ExternalLink, Loader2, Pencil, Plus, RefreshCw,
  Trash2, PanelsTopLeft, ArrowUpCircle, Radar,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_BADGE = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  auth_failed: "bg-red-50 text-red-700 border-red-200",
  unreachable: "bg-red-50 text-red-700 border-red-200",
  waf_blocked: "bg-orange-50 text-orange-800 border-orange-200",
  no_auth: "bg-amber-50 text-amber-800 border-amber-200",
  unchecked: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const EMPTY_SITE = { name: "", url: "", client_id: "", username: "", app_password: "", woo_key: "", woo_secret: "" };

/* ── Sites registry tab ─────────────────────────────────────────── */
function SitesTab({ sites, reload }) {
  const [dialog, setDialog] = useState(null);
  const [clients, setClients] = useState([]);
  const [checking, setChecking] = useState("");
  const [saving, setSaving] = useState(false);
  const [outboundIp, setOutboundIp] = useState(null);
  const [detectingIp, setDetectingIp] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const detectIp = async () => {
    setDetectingIp(true);
    try {
      const { data } = await api.get("/wp/outbound-ip");
      setOutboundIp(data.ip);
      try { await navigator.clipboard.writeText(data.ip); } catch { /* clipboard blocked */ }
      toast.success(`Outbound IP: ${data.ip} — copied to clipboard`, { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not detect outbound IP");
    } finally { setDetectingIp(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...dialog, client_id: dialog.client_id || null };
      if (dialog.site_id) await api.put(`/wp/sites/${dialog.site_id}`, body);
      else await api.post("/wp/sites", body);
      setDialog(null); reload();
      toast.success("Site saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const check = async (s) => {
    setChecking(s.site_id);
    try {
      const { data } = await api.post(`/wp/sites/${s.site_id}/check`);
      const tone = data.status === "ok" ? "success"
        : data.status === "waf_blocked" ? "error" : "warning";
      const msg = data.status_reason
        ? `${s.name}: ${data.status} — ${data.status_reason}`
        : `${s.name}: ${data.status}`;
      toast[tone](msg, { duration: data.status_reason ? 12000 : 4000 });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setChecking(""); }
  };

  const del = async (s) => {
    if (!window.confirm(`Remove site "${s.name}"? (does not touch the actual website)`)) return;
    await api.delete(`/wp/sites/${s.site_id}`); reload();
  };

  const downloadBeacon = async (s) => {
    try {
      const { data } = await api.get(`/wp/sites/${s.site_id}/beacon-download`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "iceberg-beacon.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success(
        "Iceberg Beacon downloaded. Install on the WP site → the site "
        + "will start polling us every 15 min. Bypasses Imunify360.");
    } catch { toast.error("Download failed"); }
  };

  const [beaconPanel, setBeaconPanel] = useState(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-4">
        <div className="max-w-2xl">
          <p className="text-sm text-zinc-500">
            Connect each client WordPress site once. The recommended path is
            the <b>Iceberg Beacon</b> plugin — the site polls us outbound
            every 5-60 min, so no client-side firewall (Imunify360,
            Wordfence, Cloudflare) can block us. Application Passwords
            + Woo keys are optional and only needed when the beacon is
            not installed.
          </p>
          <div className="mt-2 text-xs">
            <Link to="/wp-beacon" className="text-[#002FA7] hover:underline inline-flex items-center gap-1"
              data-testid="link-beacon-dashboard">
              <Radar className="h-3 w-3" /> Open the master Beacon dashboard →
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={detectIp} disabled={detectingIp}
            data-testid="detect-outbound-ip-btn" title="Legacy: shows the public IP this app uses when talking to WordPress hosts. Only relevant if you can't install the Beacon plugin and must rely on inbound REST calls (Imunify360 will typically block those).">
            {detectingIp
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Activity className="h-4 w-4 mr-1" />}
            {outboundIp ? `IP: ${outboundIp}` : "Detect outbound IP"}
          </Button>
          <Button onClick={() => setDialog({ ...EMPTY_SITE })} data-testid="add-site-btn">
            <Plus className="h-4 w-4 mr-1" /> Add site
          </Button>
        </div>
      </div>
      {outboundIp && (
        <div className="text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded-md p-3"
             data-testid="outbound-ip-hint">
          <b>Outbound IP:</b> <span className="font-mono">{outboundIp}</span>{" "}
          — allowlist this in <b>cPanel → Imunify360 → Ignore List (IPs)</b> on each WordPress
          host (or the equivalent Cloudflare / Wordfence / Sucuri panel), then re-run the health
          check. IP already copied to your clipboard.
        </div>
      )}
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
              <th className="p-3">Site</th><th className="p-3">Status</th>
              <th className="p-3">Detected</th><th className="p-3">Auth</th>
              <th className="p-3">Last checked</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => {
              const d = s.detected || {};
              return (
                <tr key={s.site_id} className="border-b" data-testid={`site-row-${s.site_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{s.name}</div>
                    <a href={s.url} target="_blank" rel="noreferrer"
                      className="text-xs text-[#002FA7] hover:underline">
                      {s.url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3 inline" />
                    </a>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className={STATUS_BADGE[s.status] || STATUS_BADGE.unchecked}>
                      {s.status}
                    </Badge>
                    {s.status_reason && (
                      <div className="text-xs text-orange-700 mt-1 max-w-xs leading-snug"
                           data-testid={`site-status-reason-${s.site_id}`}>
                        {s.status_reason}
                      </div>
                    )}
                  </td>
                  <td className="p-3 space-x-1">
                    {d.breakdance && <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">BD {d.breakdance.version}</Badge>}
                    {(d.woocommerce || d.woocommerce_api) && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Woo{d.woocommerce ? ` ${d.woocommerce.version}` : ""}</Badge>}
                    {(d.connector || d.connector_api) && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Connector</Badge>}
                    {d.plugins_total != null && <span className="text-xs text-zinc-400">{d.plugins_active}/{d.plugins_total} plugins</span>}
                  </td>
                  <td className="p-3 text-xs">
                    <div>{s.has_wp_auth ? "✓ WP app password" : "— no WP auth"}</div>
                    <div>{s.has_woo_keys ? "✓ Woo keys" : "— no Woo keys"}</div>
                  </td>
                  <td className="p-3 text-xs text-zinc-500">
                    {s.last_checked_at ? s.last_checked_at.slice(0, 16).replace("T", " ") : "never"}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" title="Health check"
                      disabled={checking === s.site_id} onClick={() => check(s)}
                      data-testid={`check-site-${s.site_id}`}>
                      {checking === s.site_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm"
                      title="Download Iceberg Beacon plugin (outbound — bypasses Imunify360)"
                      onClick={() => downloadBeacon(s)}
                      className="text-[#002FA7] hover:bg-[#002FA7]/10"
                      data-testid={`beacon-download-${s.site_id}`}>
                      <Radar className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" title="Open beacon panel"
                      onClick={() => setBeaconPanel(s.site_id)}
                      data-testid={`beacon-open-${s.site_id}`}>
                      <PanelsTopLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => setDialog({ ...EMPTY_SITE, ...s, app_password: "", woo_key: "", woo_secret: "" })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(s)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!sites.length && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-400">
                No sites connected yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="site-dialog">
          <DialogHeader><DialogTitle>{dialog?.site_id ? "Edit site" : "Connect a WordPress site"}</DialogTitle>
            <DialogDescription>Credentials are encrypted at rest and never shown again.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <Input placeholder="Site name (e.g. GBE Group)" value={dialog.name}
                data-testid="site-name-input"
                onChange={(e) => setDialog({ ...dialog, name: e.target.value })} />
              <Input placeholder="https://clientsite.com.au" value={dialog.url}
                data-testid="site-url-input"
                onChange={(e) => setDialog({ ...dialog, url: e.target.value })} />
              <Select value={dialog.client_id || "none"}
                onValueChange={(v) => setDialog({ ...dialog, client_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="site-client-select">
                  <SelectValue placeholder="Link to client (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— no client —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs font-semibold text-zinc-500 pt-1">WordPress admin access</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="WP admin username" value={dialog.username || ""}
                  data-testid="site-username-input"
                  onChange={(e) => setDialog({ ...dialog, username: e.target.value })} />
                <Input type="password" value={dialog.app_password}
                  placeholder={dialog.site_id ? "App password (keep saved)" : "Application password"}
                  data-testid="site-password-input"
                  onChange={(e) => setDialog({ ...dialog, app_password: e.target.value })} />
              </div>
              <div className="text-xs font-semibold text-zinc-500 pt-1">WooCommerce REST keys (optional)</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Consumer key (ck_…)" value={dialog.woo_key}
                  onChange={(e) => setDialog({ ...dialog, woo_key: e.target.value })} />
                <Input type="password" placeholder="Consumer secret (cs_…)" value={dialog.woo_secret}
                  onChange={(e) => setDialog({ ...dialog, woo_secret: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} disabled={saving || !dialog?.name || !dialog?.url}
              data-testid="site-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BeaconPanel siteId={beaconPanel} onClose={() => setBeaconPanel(null)} />
    </div>
  );
}

/* ── BeaconPanel ───────────────────────────────────────────────────
 *  Per-site dialog showing:
 *    • Beacon health (last-seen, current interval, snapshot summary)
 *    • Interval selector (5/15/60 min) — plugin picks up on next poll
 *    • Command queue with 10s polling while any command is pending
 *    • Quick-action buttons for the common command types
 * ---------------------------------------------------------------- */
function BeaconPanel({ siteId, onClose }) {
  const [status, setStatus] = useState(null);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const open = !!siteId;

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get(`/wp/sites/${siteId}/beacon-status`),
        api.get(`/wp/sites/${siteId}/beacon-commands?limit=30`),
      ]);
      setStatus(s.data); setCommands(c.data.commands || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Poll every 10s while any command is pending/dispatched so the UI
  // reflects the beacon coming back with results in near-real time.
  useEffect(() => {
    if (!open) return;
    const hasPending = commands.some((c) =>
      c.status === "pending" || c.status === "dispatched");
    if (!hasPending) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [open, commands, load]);

  const changeInterval = async (mins) => {
    try {
      await api.patch(`/wp/sites/${siteId}/beacon-interval`,
        { interval_minutes: mins });
      toast.success(`Beacon interval set to ${mins} min (takes effect on next poll).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const queueCommand = async (type, params) => {
    try {
      await api.post(`/wp/sites/${siteId}/beacon-commands`, { type, params });
      toast.success(`Queued: ${type}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Queue failed"); }
  };

  const rotateToken = async () => {
    if (!window.confirm(
      "Rotate the beacon token? The currently-installed plugin will "
      + "stop working until you re-download and re-upload it.")) return;
    try {
      await api.post(`/wp/sites/${siteId}/beacon-rotate-token`);
      toast.success("Token rotated — re-download the plugin zip.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rotation failed"); }
  };

  const snap = status?.beacon_last_snapshot || {};
  const pu = snap.plugin_updates || [];
  const tu = snap.theme_updates || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[90vh] overflow-y-auto"
        data-testid="beacon-panel">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
            <Radar className="h-4 w-4 text-[#002FA7]" /> Beacon control
          </DialogTitle>
        </DialogHeader>
        {loading && !status ? (
          <div className="py-6 flex items-center gap-2 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !status?.beacon_configured ? (
          <div className="py-6 text-sm text-zinc-500 space-y-2">
            <div>No beacon token yet on this site.</div>
            <div>Click the <Radar className="h-3 w-3 inline mx-0.5 text-[#002FA7]" />
              icon on the site row to download the plugin — a token
              will be generated as part of the download.</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatCard label="Last seen" value={status.beacon_last_seen_at
                ? new Date(status.beacon_last_seen_at).toLocaleString("en-AU",
                  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                : "never"} />
              <StatCard label="Plugin updates" value={pu.length}
                highlight={pu.length > 0 ? "orange" : null} />
              <StatCard label="Theme updates" value={tu.length}
                highlight={tu.length > 0 ? "orange" : null} />
              <StatCard label="Pending cmds" value={status.pending_commands}
                highlight={status.pending_commands > 0 ? "blue" : null} />
            </div>

            <div className="flex items-center gap-2 text-xs flex-wrap">
              <span className="text-zinc-500">Poll interval:</span>
              {[5, 15, 60].map((m) => (
                <Button key={m} size="sm" variant="outline"
                  onClick={() => changeInterval(m)}
                  data-testid={`beacon-interval-${m}`}
                  className="h-7 rounded-sm">{m} min</Button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={rotateToken}
                  className="h-7 rounded-sm text-red-600 hover:bg-red-50"
                  data-testid="beacon-rotate-token">
                  Rotate token
                </Button>
              </div>
            </div>

            <div className="border-t border-zinc-200 pt-3">
              <div className="label-overline mb-2">Quick actions</div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-7 rounded-sm"
                  onClick={() => queueCommand("breakdance_clear_cache")}
                  data-testid="beacon-cmd-bd-clear">Clear Breakdance cache</Button>
                <Button size="sm" variant="outline" className="h-7 rounded-sm"
                  onClick={() => queueCommand("wp_clear_object_cache")}
                  data-testid="beacon-cmd-wp-flush">Flush WP object cache</Button>
                <Button size="sm" variant="outline" className="h-7 rounded-sm"
                  onClick={() => queueCommand("flush_rewrite_rules")}
                  data-testid="beacon-cmd-rewrites">Flush rewrite rules</Button>
                {snap.core_update_available && (
                  <Button size="sm" className="h-7 rounded-sm bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => queueCommand("update_core")}
                    data-testid="beacon-cmd-update-core">
                    Update WP core to {snap.core_update_available}
                  </Button>
                )}
              </div>
            </div>

            {pu.length > 0 && (
              <div className="border-t border-zinc-200 pt-3">
                <div className="label-overline mb-2">Outdated plugins on this site</div>
                <div className="space-y-1">
                  {pu.map((p) => (
                    <div key={p.plugin}
                      className="flex items-center gap-2 text-xs border border-zinc-100 rounded-sm p-2">
                      <div className="flex-1">
                        <div className="font-semibold">{p.name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">
                          {p.current} → {p.new}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm"
                        onClick={() => queueCommand("update_plugin", { plugin: p.plugin, name: p.name, target_version: p.new })}
                        data-testid={`beacon-cmd-update-plugin-${p.plugin.replace(/[^a-z0-9]/gi, "-")}`}>
                        <ArrowUpCircle className="h-3 w-3 mr-1" /> Queue update
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-zinc-200 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="label-overline">Recent commands</div>
                <Button variant="ghost" size="sm" onClick={load}
                  className="h-6 rounded-sm text-[10px]"
                  data-testid="beacon-refresh-commands">
                  <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </div>
              {commands.length === 0 ? (
                <div className="text-[11px] text-zinc-400 italic">
                  No commands queued yet.
                </div>
              ) : (
                <div className="space-y-1" data-testid="beacon-command-list">
                  {commands.map((c) => (
                    <div key={c.command_id}
                      className="flex items-center gap-2 text-[11px] border border-zinc-100 rounded-sm p-2"
                      data-testid={`beacon-cmd-row-${c.command_id}`}>
                      <span className="font-mono text-[10px] text-zinc-400">{c.command_id.slice(0, 12)}</span>
                      <span className="font-semibold">{c.type}</span>
                      {c.params?.plugin && <span className="text-zinc-500">· {c.params.plugin}</span>}
                      {c.params?.stylesheet && <span className="text-zinc-500">· {c.params.stylesheet}</span>}
                      <Badge variant="outline" className={`ml-auto rounded-sm text-[9px] ${
                        c.status === "complete" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : c.status === "failed" ? "bg-red-50 text-red-700 border-red-200"
                            : c.status === "dispatched" ? "bg-sky-50 text-sky-700 border-sky-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {c.status}
                      </Badge>
                      <span className="text-[9px] font-mono text-zinc-400">
                        {String(c.created_at || "").slice(11, 16)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, highlight }) {
  const colors = {
    orange: "border-orange-200 bg-orange-50 text-orange-800",
    blue: "border-[#002FA7]/30 bg-[#002FA7]/5 text-[#002FA7]",
  }[highlight] || "border-zinc-200 bg-white text-zinc-900";
  return (
    <div className={`rounded-sm border p-2 ${colors}`}>
      <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="font-display font-bold text-lg mt-0.5">{value ?? "—"}</div>
    </div>
  );
}


/* ── Shared: beacon-only mode detection + banner ─────────────────
 *  Every legacy REST-based tab (Content, Themes, Comments, Users,
 *  Site Settings) hits the WordPress `/wp-json/...` endpoints. That
 *  path is blocked on Imunify360-protected hosts.  When we see the
 *  characteristic error (401/403 auth_failed, 502 upstream, or
 *  Cloudflare "invalid or incomplete response") AND the site has a
 *  fresh beacon, swap the error for a friendly explanation +
 *  actionable next-steps.
 * ---------------------------------------------------------------- */
function useBeaconMode(siteId) {
  const [state, setState] = useState({
    known: false, beaconOnly: false, hasWpAuth: false,
    siteUrl: "", lastSeen: null,
  });
  useEffect(() => {
    if (!siteId) return;
    (async () => {
      try {
        const { data } = await api.get(`/wp/sites/${siteId}/beacon-status`);
        setState({
          known: true,
          beaconOnly: !!data?.beacon_configured && !!data?.beacon_last_seen_at,
          hasWpAuth: !!data?.has_wp_auth,
          siteUrl: data?.site_url || "",
          lastSeen: data?.beacon_last_seen_at || null,
        });
      } catch {
        setState({ known: true, beaconOnly: false, hasWpAuth: false,
                   siteUrl: "", lastSeen: null });
      }
    })();
  }, [siteId]);
  return state;
}

function isBeaconBlockingError(err) {
  const status = err?.response?.status;
  const detail = (err?.response?.data?.detail || err?.message || "").toLowerCase();
  return status === 401 || status === 403 || status === 502 || status === 503
    || detail.includes("auth_failed")
    || detail.includes("authentication failed")
    || detail.includes("incomplete response")
    || detail.includes("origin web server");
}

function BeaconOnlyNotice({ siteId, tab, lastSeen, siteUrl }) {
  const cleanUrl = (siteUrl || "").replace(/\/$/, "");
  return (
    <div className="rounded-sm bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 space-y-3"
      data-testid={`beacon-only-notice-${tab}`}>
      <div className="flex items-center gap-2 font-semibold">
        <Radar className="h-4 w-4" /> Beacon-only mode — {tab} not available via REST
      </div>
      <p>
        The <b>Iceberg Beacon</b> plugin is active on this site (last poll{" "}
        {lastSeen ? new Date(lastSeen).toLocaleString("en-AU",
          { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
          : "recently"}
        ), but this tab needs inbound WordPress REST access to
        <code className="mx-1 bg-amber-100 rounded-sm px-1">/wp-json/wp/v2/*</code>
        which the client&apos;s WAF (Imunify360 / Cloudflare) is currently
        blocking. Because no <b>Application Password</b> is stored for
        this site either, we can&apos;t authenticate the REST call.
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" data-testid={`beacon-notice-open-panel-${tab}`}
          onClick={() => window.open(`/wp-beacon?site=${siteId}`, "_blank")}>
          <PanelsTopLeft className="h-3.5 w-3.5 mr-1.5" />
          Open Beacon Panel
        </Button>
        {cleanUrl && (
          <Button size="sm" variant="outline" data-testid={`beacon-notice-app-pass-${tab}`}
            onClick={() => window.open(`${cleanUrl}/wp-admin/profile.php#application-passwords-section`, "_blank")}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Create Application Password on site
          </Button>
        )}
      </div>
      <details className="text-[12px] pt-1">
        <summary className="cursor-pointer text-amber-900/80">More options</summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li>
            Preferred — use the <b>Beacon Panel</b> for Plugins, Themes,
            Updates and cache clears (that data ships in every beacon
            snapshot).
          </li>
          <li>
            Or generate an <b>Application Password</b> at
            <code className="mx-1 bg-amber-100 rounded-sm px-1">
              /wp-admin/profile.php#application-passwords-section
            </code>
            , then paste it into the Sites row (Edit → Application
            Password). REST tabs will start working immediately.
          </li>
          <li>
            Or ask the host to whitelist our egress IP in Imunify360 /
            Cloudflare bot-fight (use the <b>Detect outbound IP</b>
            button at the top of the Sites tab).
          </li>
        </ol>
      </details>
    </div>
  );
}
export function ContentTab({ siteId }) {
  const [type, setType] = useState("pages");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const beacon = useBeaconMode(siteId);

  // If the site is a "beacon-only" host (beacon phoning home + no
  // stored WP application password) we KNOW every /wp-json REST call
  // will fail — skip the REST attempt entirely and render the notice.
  // This eliminates the 502-timeout + red toast the user was seeing on
  // Content/Users/Comments/Themes/Settings tabs.
  const skipRest = beacon.beaconOnly && !beacon.hasWpAuth;

  const load = useCallback(async () => {
    if (!siteId || skipRest) return;
    setLoading(true); setLoadError(null);
    try {
      const { data } = await api.get(`/wp/sites/${siteId}/content`,
        { params: { type, search } });
      setData(data);
    } catch (e) {
      setLoadError(e);
      setData(null);
      if (!isBeaconBlockingError(e)) {
        toast.error(e.response?.data?.detail || "Failed to load content");
      }
    }
    finally { setLoading(false); }
  }, [siteId, type, search, skipRest]);
  useEffect(() => { load(); }, [load]);

  // Beacon-only fallback — REST is blocked; show explanation, not error.
  if (skipRest || (loadError && beacon.beaconOnly && isBeaconBlockingError(loadError))) {
    return <BeaconOnlyNotice siteId={siteId} tab="Content" lastSeen={beacon.lastSeen} siteUrl={beacon.siteUrl} />;
  }

  const openEditor = async (row) => {
    if (!row) { setEditor({ id: null, type, title: "", content: "", status: "draft" }); return; }
    try {
      const { data } = await api.get(`/wp/sites/${siteId}/content/${row.id}`, { params: { type } });
      setEditor({ ...data, type });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to open"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { type: editor.type, title: editor.title, content: editor.content, status: editor.status };
      if (editor.id) await api.put(`/wp/sites/${siteId}/content/${editor.id}`, body);
      else await api.post(`/wp/sites/${siteId}/content`, body);
      toast.success("Saved to the live site");
      setEditor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-32" data-testid="content-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pages">Pages</SelectItem>
            <SelectItem value="posts">Posts</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Search…" value={search} className="w-56"
          data-testid="content-search-input"
          onChange={(e) => setSearch(e.target.value)} />
        <Button variant="outline" size="icon" onClick={load}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <div className="flex-1" />
        <Button onClick={() => openEditor(null)} data-testid="new-content-btn">
          <Plus className="h-4 w-4 mr-1" /> New {type === "pages" ? "page" : "post"}
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
            <th className="p-3">Title</th><th className="p-3">Status</th>
            <th className="p-3">Modified</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.id} className="border-b" data-testid={`content-row-${r.id}`}>
                <td className="p-3 font-medium">{r.title || "(no title)"}</td>
                <td className="p-3"><Badge variant="outline">{r.status}</Badge></td>
                <td className="p-3 text-xs text-zinc-500">{(r.modified || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <a href={r.link} target="_blank" rel="noreferrer">
                    <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                  </a>
                  <Button variant="ghost" size="sm" onClick={() => openEditor(r)}
                    data-testid={`edit-content-${r.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {data && !data.rows.length && (
              <tr><td colSpan={4} className="p-8 text-center text-zinc-400">Nothing found.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-3xl" data-testid="content-editor-dialog">
          <DialogHeader><DialogTitle>
            {editor?.id ? `Edit ${editor?.type === "pages" ? "page" : "post"} #${editor?.id}` : `New ${type === "pages" ? "page" : "post"}`}
          </DialogTitle>
            <DialogDescription>Changes save directly to the live client site.</DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <Input placeholder="Title" value={editor.title} data-testid="content-title-input"
                onChange={(e) => setEditor({ ...editor, title: e.target.value })} />
              <Textarea rows={14} placeholder="Content (HTML supported)" value={editor.content}
                className="font-mono text-xs" data-testid="content-body-input"
                onChange={(e) => setEditor({ ...editor, content: e.target.value })} />
              <div className="flex items-center gap-2">
                <Select value={editor.status}
                  onValueChange={(v) => setEditor({ ...editor, status: v })}>
                  <SelectTrigger className="w-36" data-testid="content-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="publish">Published</SelectItem>
                    <SelectItem value="pending">Pending review</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-zinc-400">
                  Note: pages built with Breakdance are designed in the builder — editing raw
                  content here won&apos;t change the Breakdance design.
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} disabled={saving || !editor?.title} data-testid="content-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save to site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Plugins tab ────────────────────────────────────────────────── */
export function PluginsTab({ siteId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [installSlug, setInstallSlug] = useState("");
  const [source, setSource] = useState("rest");  // "beacon" | "rest"

  const load = useCallback(async () => {
    if (!siteId) return;
    // Beacon-first: if the site has a fresh beacon snapshot use its
    // installed_plugins list — this bypasses Imunify360 completely
    // (no inbound REST call to the WP host). Falls back to the REST
    // endpoint only when there's no beacon data yet.
    try {
      const snap = await api.get(`/wp/sites/${siteId}/beacon-snapshot`);
      const installed = snap.data?.installed_plugins || [];
      if (installed.length > 0) {
        const updates = new Set((snap.data?.plugin_updates || []).map((p) => p.plugin));
        setRows(installed.map((p) => ({
          plugin: p.plugin, name: p.name, version: p.version,
          status: p.active ? "active" : "inactive",
          update: updates.has(p.plugin) ? "available" : "none",
        })));
        setSource("beacon");
        return;
      }
    } catch { /* fall through to REST */ }
    try {
      const { data } = await api.get(`/wp/sites/${siteId}/plugins`);
      setRows(data);
      setSource("rest");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load plugins"); setRows([]); }
  }, [siteId]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const act = async (key, body, ok) => {
    if (source === "beacon") {
      // Beacon-only site: queue the equivalent command instead of
      // hitting the (inbound-blocked) REST endpoint.
      const type = body.action === "activate" ? null
        : body.action === "deactivate" ? null
        : body.action === "update" ? "update_plugin"
        : null;
      if (!type) {
        toast.error("This action requires REST access — install an "
          + "Application Password on the site to enable activate/deactivate.");
        return;
      }
      setBusy(key);
      try {
        await api.post(`/wp/sites/${siteId}/beacon-commands`,
          { type, params: { plugin: body.plugin } });
        toast.success(`${ok} — queued to run on next beacon (5-60 min).`);
      } catch (e) { toast.error(e.response?.data?.detail || "Queue failed"); }
      finally { setBusy(""); }
      return;
    }
    setBusy(key);
    try {
      await api.put(`/wp/sites/${siteId}/plugins`, body);
      toast.success(ok); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const runUpdate = async (p) => {
    setBusy(`up-${p.plugin}`);
    try {
      await api.post(`/wp/sites/${siteId}/updates/plugin`, { plugin: p.plugin });
      toast.success(`${p.name} updated`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(""); }
  };

  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <div className="space-y-3">
      {source === "beacon" && (
        <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-2 flex items-start gap-2"
          data-testid="plugins-tab-beacon-banner">
          <Radar className="h-3 w-3 mt-0.5 shrink-0" />
          <div>
            <b>Beacon mode</b> — data pulled from the last WP beacon (no
            inbound REST call). Update actions are queued for the next
            beacon poll and may take 5-60 min to reflect. Install /
            activate / deactivate need Application Password auth
            (unsupported in beacon-only mode).
          </div>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <Input placeholder="Install from wordpress.org (slug, e.g. wordfence)" className="w-80"
          value={installSlug} data-testid="plugin-install-input"
          onChange={(e) => setInstallSlug(e.target.value)} />
        <Button variant="outline" disabled={!installSlug || busy === "install"}
          data-testid="plugin-install-btn"
          onClick={() => act("install", { slug: installSlug.trim() }, "Plugin installed")}>
          {busy === "install" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          Install
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="icon" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
            <th className="p-3">Plugin</th><th className="p-3">Version</th>
            <th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.plugin} className="border-b" data-testid={`plugin-row-${p.plugin}`}>
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3">
                  {p.version}
                  {p.update_available && (
                    <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-200">
                      → {p.update_available}
                    </Badge>
                  )}
                </td>
                <td className="p-3">
                  <Badge variant="outline" className={p.status === "active"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                    {p.status}
                  </Badge>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {p.update_available && (
                    <Button variant="outline" size="sm" className="mr-1"
                      disabled={busy === `up-${p.plugin}`}
                      data-testid={`plugin-update-${p.plugin}`}
                      onClick={() => runUpdate(p)}>
                      {busy === `up-${p.plugin}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpCircle className="h-4 w-4 mr-1" />}
                      Update
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" disabled={busy === p.plugin}
                    data-testid={`plugin-toggle-${p.plugin}`}
                    onClick={() => act(p.plugin,
                      { plugin: p.plugin, status: p.status === "active" ? "inactive" : "active" },
                      p.status === "active" ? "Deactivated" : "Activated")}>
                    {busy === p.plugin ? <Loader2 className="h-4 w-4 animate-spin" />
                      : p.status === "active" ? "Deactivate" : "Activate"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

/* ── Themes / Updates / Comments / Users / Settings tabs ────────── */
export function ThemesTab({ siteId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [source, setSource] = useState("rest");
  const [loadError, setLoadError] = useState(null);
  const beacon = useBeaconMode(siteId);
  // Skip the REST fallback entirely when we already know the site is
  // beacon-only + has no app password (the /themes call will 400).
  const skipRest = beacon.beaconOnly && !beacon.hasWpAuth;
  const load = useCallback(async () => {
    if (!siteId) return;
    setLoadError(null);
    // Beacon-first: v2.2.0+ snapshots include installed_themes.
    try {
      const snap = await api.get(`/wp/sites/${siteId}/beacon-snapshot`);
      const it = snap.data?.installed_themes || [];
      const tu = new Set((snap.data?.theme_updates || []).map((t) => t.stylesheet));
      if (it.length > 0) {
        setRows(it.map((t) => ({
          stylesheet: t.stylesheet, name: t.name, version: t.version,
          status: t.active ? "active" : "inactive",
          update: tu.has(t.stylesheet) ? "available" : "none",
        })));
        setSource("beacon");
        return;
      }
    } catch { /* fall through */ }
    if (skipRest) { setRows(null); return; }
    try {
      setRows((await api.get(`/wp/sites/${siteId}/themes`)).data);
      setSource("rest");
    } catch (e) {
      setLoadError(e); setRows(null);
      if (!isBeaconBlockingError(e)) {
        toast.error(e.response?.data?.detail || "Failed to load themes");
      }
    }
  }, [siteId, skipRest]);
  useEffect(() => { setRows(null); load(); }, [load]);

  // Show the beacon-only notice when we know REST is blocked. Because
  // we run the beacon-snapshot lookup first, `rows` will still be set
  // from a fresh v2.2.0 snapshot — we only fall to this branch when
  // the snapshot didn't ship themes AND REST is blocked.
  if ((skipRest && !rows)
      || (loadError && beacon.beaconOnly && isBeaconBlockingError(loadError))) {
    return <BeaconOnlyNotice siteId={siteId} tab="Themes" lastSeen={beacon.lastSeen} siteUrl={beacon.siteUrl} />;
  }

  const update = async (t) => {
    setBusy(t.stylesheet);
    try {
      await api.post(`/wp/sites/${siteId}/updates/theme`, { stylesheet: t.stylesheet });
      toast.success(`${t.name} updated`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(""); }
  };

  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <Card><CardContent className="p-0">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
          <th className="p-3">Theme</th><th className="p-3">Version</th>
          <th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
        </tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.stylesheet} className="border-b" data-testid={`theme-row-${t.stylesheet}`}>
              <td className="p-3 font-medium">{t.name}</td>
              <td className="p-3">{t.version}
                {t.update_available && (
                  <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-200">
                    → {t.update_available}
                  </Badge>
                )}
              </td>
              <td className="p-3">
                <Badge variant="outline" className={t.status === "active"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-zinc-100 text-zinc-500 border-zinc-200"}>{t.status}</Badge>
              </td>
              <td className="p-3 text-right">
                {t.update_available && (
                  <Button variant="outline" size="sm" disabled={busy === t.stylesheet}
                    data-testid={`theme-update-${t.stylesheet}`} onClick={() => update(t)}>
                    {busy === t.stylesheet ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpCircle className="h-4 w-4 mr-1" />}
                    Update
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

export function UpdatesTab({ siteId }) {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [source, setSource] = useState("rest");
  const load = useCallback(async () => {
    if (!siteId) return;
    setErr(""); setStatus(null);
    // Beacon-first: prefer the last beacon snapshot (no inbound REST).
    try {
      const snap = await api.get(`/wp/sites/${siteId}/beacon-snapshot`);
      if (snap.data?.beacon_last_seen_at) {
        setStatus({
          wp_version: snap.data.wp_version,
          php_version: snap.data.php_version,
          core_update_available: snap.data.core_update_available,
          plugin_updates: snap.data.plugin_updates || [],
          theme_updates: snap.data.theme_updates || [],
        });
        setSource("beacon");
        return;
      }
    } catch { /* fall through to REST */ }
    try {
      setStatus((await api.get(`/wp/sites/${siteId}/updates`)).data);
      setSource("rest");
    } catch (e) {
      setErr(e.response?.data?.detail
        || "Neither the Beacon plugin nor the legacy Connector is reachable. "
        + "Install the Iceberg Beacon plugin from the Sites tab.");
    }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  if (err) return (
    <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
      data-testid="updates-connector-warning">
      {err}
    </div>
  );
  if (!status) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <div className="space-y-4" data-testid="updates-overview">
      {source === "beacon" && (
        <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-2 flex items-start gap-2">
          <Radar className="h-3 w-3 mt-0.5 shrink-0" />
          <b>Beacon mode</b> — data from the last WP beacon.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["WordPress core", status.wp_version, status.core_update_available ? `Update to ${status.core_update_available}` : "Up to date"],
          ["PHP", status.php_version, null],
          ["Plugin updates", (status.plugin_updates || []).length, null],
          ["Theme updates", (status.theme_updates || []).length, null]].map(([l, v, sub]) => (
          <Card key={l}><CardContent className="p-4">
            <div className="text-xs text-zinc-500">{l}</div>
            <div className="text-xl font-bold">{v}</div>
            {sub && <div className={`text-xs ${String(sub).startsWith("Update") ? "text-amber-700" : "text-emerald-600"}`}>{sub}</div>}
          </CardContent></Card>
        ))}
      </div>
      {(status.plugin_updates || []).length > 0 && (
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">Pending plugin updates</div>
          {status.plugin_updates.map((u) => (
            <div key={u.plugin} className="flex justify-between border-b last:border-0 py-1.5 text-sm">
              <span>{u.name}</span><span className="text-zinc-500">{u.current} → <b>{u.new}</b></span>
            </div>
          ))}
          <div className="text-xs text-zinc-400 mt-2">Run updates from the Plugins tab.</div>
        </CardContent></Card>
      )}
    </div>
  );
}

export function CommentsTab({ siteId }) {
  const [status, setStatus] = useState("hold");
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const beacon = useBeaconMode(siteId);
  const skipRest = beacon.beaconOnly && !beacon.hasWpAuth;
  const load = useCallback(async () => {
    if (!siteId || skipRest) return;
    setLoadError(null);
    try { setData((await api.get(`/wp/sites/${siteId}/comments`, { params: { status } })).data); }
    catch (e) {
      setLoadError(e); setData({ rows: [] });
      if (!isBeaconBlockingError(e)) {
        toast.error(e.response?.data?.detail || "Failed to load comments");
      }
    }
  }, [siteId, status, skipRest]);
  useEffect(() => { setData(null); load(); }, [load]);

  const moderate = async (c, newStatus) => {
    try {
      await api.put(`/wp/sites/${siteId}/comments/${c.id}`, { status: newStatus });
      toast.success(`Comment ${newStatus}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (skipRest || (loadError && beacon.beaconOnly && isBeaconBlockingError(loadError))) {
    return <BeaconOnlyNotice siteId={siteId} tab="Comments" lastSeen={beacon.lastSeen} siteUrl={beacon.siteUrl} />;
  }

  return (
    <div className="space-y-3">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="w-44" data-testid="comments-status-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="hold">Pending</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="spam">Spam</SelectItem>
        </SelectContent>
      </Select>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : (
        <Card><CardContent className="p-4 space-y-3">
          {data.rows.map((c) => (
            <div key={c.id} className="border-b last:border-0 pb-3 text-sm" data-testid={`comment-${c.id}`}>
              <div className="flex justify-between">
                <b>{c.author_name || "Anonymous"}</b>
                <span className="text-xs text-zinc-400">{(c.date || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="text-zinc-600"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.content) }} />
              <div className="flex gap-1 mt-1">
                {status !== "approved" && <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => moderate(c, "approved")}>Approve</Button>}
                {status !== "spam" && <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => moderate(c, "spam")}>Spam</Button>}
                <Button size="sm" variant="outline" className="h-6 text-xs text-red-600" onClick={() => moderate(c, "trash")}>Trash</Button>
              </div>
            </div>
          ))}
          {!data.rows.length && <div className="text-zinc-400 text-sm">No {status} comments.</div>}
        </CardContent></Card>
      )}
    </div>
  );
}

export function UsersTab({ siteId }) {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const beacon = useBeaconMode(siteId);
  const skipRest = beacon.beaconOnly && !beacon.hasWpAuth;
  useEffect(() => {
    if (!siteId || skipRest) return;
    setRows(null); setLoadError(null);
    api.get(`/wp/sites/${siteId}/users`).then((r) => setRows(r.data))
      .catch((e) => {
        setLoadError(e); setRows([]);
        if (!isBeaconBlockingError(e)) {
          toast.error(e.response?.data?.detail || "Failed to load users");
        }
      });
  }, [siteId, skipRest]);
  if (skipRest || (loadError && beacon.beaconOnly && isBeaconBlockingError(loadError))) {
    return <BeaconOnlyNotice siteId={siteId} tab="Users" lastSeen={beacon.lastSeen} siteUrl={beacon.siteUrl} />;
  }
  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <Card><CardContent className="p-0">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
          <th className="p-3">Name</th><th className="p-3">Username</th>
          <th className="p-3">Email</th><th className="p-3">Roles</th>
        </tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className="border-b" data-testid={`user-row-${u.id}`}>
              <td className="p-3 font-medium">{u.name}</td>
              <td className="p-3">{u.username}</td>
              <td className="p-3">{u.email}</td>
              <td className="p-3">{(u.roles || []).join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

export function WpSettingsTab({ siteId }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const beacon = useBeaconMode(siteId);
  const skipRest = beacon.beaconOnly && !beacon.hasWpAuth;
  useEffect(() => {
    if (!siteId || skipRest) return;
    setS(null); setLoadError(null);
    api.get(`/wp/sites/${siteId}/settings`).then((r) => setS(r.data))
      .catch((e) => {
        setLoadError(e);
        if (!isBeaconBlockingError(e)) {
          toast.error(e.response?.data?.detail || "Failed to load settings");
        }
      });
  }, [siteId, skipRest]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/wp/sites/${siteId}/settings`,
        { title: s.title, description: s.description });
      toast.success("Site settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (skipRest || (loadError && beacon.beaconOnly && isBeaconBlockingError(loadError))) {
    return <BeaconOnlyNotice siteId={siteId} tab="Site settings" lastSeen={beacon.lastSeen} siteUrl={beacon.siteUrl} />;
  }
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <Card><CardContent className="p-5 space-y-3 max-w-xl">
      <div>
        <div className="text-xs font-medium text-zinc-500 mb-1">Site title</div>
        <Input value={s.title || ""} data-testid="wp-settings-title"
          onChange={(e) => setS({ ...s, title: e.target.value })} />
      </div>
      <div>
        <div className="text-xs font-medium text-zinc-500 mb-1">Tagline</div>
        <Input value={s.description || ""} data-testid="wp-settings-tagline"
          onChange={(e) => setS({ ...s, description: e.target.value })} />
      </div>
      <div className="text-xs text-zinc-400">
        Timezone: {s.timezone || "—"} · Language: {s.language || "—"} · Posts per page: {s.posts_per_page}
      </div>
      <Button onClick={save} disabled={saving} data-testid="wp-settings-save-btn">
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
      </Button>
    </CardContent></Card>
  );
}

/* ── Page ───────────────────────────────────────────────────────── */
export default function WordPressPage() {
  const { sites, siteId, pick, reload } = useSites();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "sites";

  // Deep-link support — when the beacon dashboard (or any other page)
  // sends the user here with `?siteId=...`, switch the active site
  // picker to that site so all tabs load its data instead of the
  // localStorage-remembered previous site.
  const urlSiteId = params.get("siteId") || "";
  React.useEffect(() => {
    if (urlSiteId && sites.some((s) => s.site_id === urlSiteId) && urlSiteId !== siteId) {
      pick(urlSiteId);
    }
  }, [urlSiteId, sites, siteId, pick]);

  // Preserve siteId in the URL when the user switches tabs, so back /
  // forward and copy-paste-share of the URL both keep the same site.
  const switchTab = (v) => {
    const next = { tab: v };
    if (siteId) next.siteId = siteId;
    setParams(next);
  };

  return (
    <div className="space-y-5" data-testid="wordpress-page">
      <div className="flex items-center gap-3">
        <PanelsTopLeft className="h-6 w-6 text-[#002FA7]" />
        <div>
          <h1 className="text-2xl font-bold">WordPress API</h1>
          <p className="text-sm text-zinc-500">
            Manage client WordPress sites remotely — content, plugins, themes, updates and more.
          </p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={switchTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="sites" data-testid="tab-sites">Sites</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="plugins" data-testid="tab-plugins">Plugins</TabsTrigger>
          <TabsTrigger value="themes" data-testid="tab-themes">Themes</TabsTrigger>
          <TabsTrigger value="updates" data-testid="tab-updates">Updates</TabsTrigger>
          <TabsTrigger value="comments" data-testid="tab-comments">Comments</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="wp-settings" data-testid="tab-wp-settings">Site settings</TabsTrigger>
        </TabsList>
        <TabsContent value="sites">
          <SitesTab sites={sites} reload={reload} />
        </TabsContent>
        {["content", "plugins", "themes", "updates", "comments", "users", "wp-settings"].map((t) => (
          <TabsContent key={t} value={t}>
            <div className="mb-3">
              <SitePicker sites={sites} siteId={siteId} onPick={pick} />
            </div>
            {siteId && {
              content: <ContentTab siteId={siteId} />,
              plugins: <PluginsTab siteId={siteId} />,
              themes: <ThemesTab siteId={siteId} />,
              updates: <UpdatesTab siteId={siteId} />,
              comments: <CommentsTab siteId={siteId} />,
              users: <UsersTab siteId={siteId} />,
              "wp-settings": <WpSettingsTab siteId={siteId} />,
            }[t]}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
